import { Button, Stack } from '@mui/material';
import { Field, Form, Formik, FormikProps } from 'formik';
import { TextField } from 'formik-mui';
import { CardPaper } from 'shared/ui/card-paper';
import { MediaTypePickerField } from 'entities/media/media-type-picker';
import { validate, toUpdateMedia, emptyValues, FormValues } from 'entities/media/media-basic-data/form-data';
import { useMediaBasicData, useUpdataMediaBasicData } from 'entities/media/media-basic-data/update-media.hook';
import { useSnack } from 'shared/ui/snack';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';
import { GeoPicker } from 'features/geo/picker/geo-picker';
import { CityPicker } from 'entities/geo/cities/city-picker';
import { RegionPicker } from 'shared/ui/formik-fields/region-picker';
import { CountryPicker } from 'shared/ui/formik-fields/country-picker';
import { useState, useEffect, useCallback } from 'react';
import { MediaMatherPickerField } from 'entities/media-picker/media-mather-picker';
import { ParentMediaCard } from '../parent-media/parent-media-card';
import { MEDIA_PLACEMENT_TYPE_MAP } from '@sr/dto';

type Props = {
  mediaId: number;
}

const safeFromMedia = (media: any): FormValues => {
  if (!media) return emptyValues;
  
  let placement = media.placement || '';
  if (!placement) {
    if (media.city?.id || media.cityId) {
      placement = 'local';
    } else if (media.region?.id || media.regionId) {
      placement = 'regional';
    } else if (media.country?.id || media.countryId) {
      placement = 'federal';
    }
  }

  return {
    name: media.name || '',
    mediaTypeId: media.mediaType?.id || media.typeId || '',
    placement: placement,
    cityId: media.city?.id || null,
    regionId: media.region?.id || null,
    countryId: media.country?.id || null,
    parentMediaId: media.parentMediaId || media.parentMedia?.id || null
  };
};

export const MediaBasicDataCard = ({ mediaId }: Props) => {
  const { media } = useMediaBasicData(mediaId);

  const { showSuccess, showError } = useSnack();
  
  const [selectedPlacement, setSelectedPlacement] = useState<string>(media?.placement ?? '');

  const update = useUpdataMediaBasicData();

  useEffect(() => {
    if (media) {
      let placement = media.placement || '';
      if (!placement) {
        if (media.cityId) {
          placement = 'local';
        } else if (media.regionId) {
          placement = 'regional';
        } else if (media.countryId) {
          placement = 'federal';
        }
      }
      setSelectedPlacement(placement);
    }
  }, [media]);

  const handleFormSubmit = async (formValues: FormValues) => {
    const updateData = toUpdateMedia(mediaId, formValues);
    return update(updateData)
      .then(() => showSuccess('Данные СМИ успешно обновлены'))
      .catch((error) => showError(`Ошибка обновления данных о СМИ: ${extractNestJsErrorMessage(error)}`));
  };

  const handlePlacementChange = useCallback((placement: string, setFieldValue: (field: string, value: any) => void) => {
    setSelectedPlacement(placement);
    setFieldValue('placement', placement);
    
  }, []);

  const renderGeoField = () => {
    switch (selectedPlacement) {
    case 'federal':
      return (
        <Field
          name='countryId'
          label='Страна'
          required
          component={CountryPicker}
        />
      );
    case 'regional':
      return (
        <Field
          name='regionId'
          label='Регион'
          required
          component={RegionPicker}
        />
      );
    case 'local':
      return (
        <Field
          name='cityId'
          label='Город'
          required
          component={CityPicker}
        />
      );
    default:
      return null;
    }
  };

  const initialValues = media ? safeFromMedia(media) : emptyValues;

  return (
    <>
      <CardPaper sx={{ p: 2 }}>
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validate={validate}
          onSubmit={handleFormSubmit}
        >
          {({ dirty, isSubmitting, isValid, setFieldValue }: FormikProps<FormValues>) => {

            const handleGeoPickerChange = (placement: string) => {
              handlePlacementChange(placement, setFieldValue);
            };

            return (
              <Form>
                <Stack direction="column" spacing={2}>
                  <Field
                    name="name"
                    label="Название"
                    component={TextField}
                  />
                  <Field
                    name="mediaTypeId"
                    select
                    label="Тип"
                    component={MediaTypePickerField}
                  />
                  <GeoPicker 
                    name='placement'
                    label='Размещение'
                    required
                    onPlacementChange={handleGeoPickerChange}
                  />
                  {renderGeoField()}
                  { media?.childrenMedia && media.childrenMedia.length === 0 && 
                  <Field 
                    name="parentMediaId"
                    mediaId={mediaId}
                    label='Материнское СМИ'
                    component={MediaMatherPickerField}
                  />
                  }
                  <Button 
                    type='submit' 
                    variant='contained' 
                    disabled={!dirty || isSubmitting || !isValid}
                  >
                  Сохранить
                  </Button>
                </Stack>
              </Form>
            );
          }}
        </Formik>
      </CardPaper>
      <ParentMediaCard media={media} />
    </>
  );
};
